import * as React from "react";
import tokenService, { MAPET_TOKEN_UPDATED_EVENT } from "../services/tokenService";
import { isLoggedOutLanding } from "../services/oidc";

let interceptorRegistered = false;

interface EsriRequestConfig {
  request: {
    interceptors?: Array<{
      urls: string | RegExp | (string | RegExp)[];
      before?: (params: any) => void;
      error?: (error: any) => Promise<unknown> | void;
    }>;
  };
}

interface MapEtInterceptorOptions {
  urlPattern: string;
  tokenServiceUrl: string;
  tokenClient: string;
}

/** Attach MapET token to Esri REST requests via query params (not layer URLs). */
export const setupMapEtRequestInterceptor = (
  esriConfig: EsriRequestConfig,
  { urlPattern, tokenServiceUrl, tokenClient }: MapEtInterceptorOptions
): void => {
  if (interceptorRegistered) {
    return;
  }
  interceptorRegistered = true;

  esriConfig.request.interceptors?.push({
    urls: urlPattern,
    before(params) {
      if (isLoggedOutLanding()) {
        return;
      }

      const applyToken = (token: string | null) => {
        if (!token) {
          return;
        }
        params.requestOptions.query = params.requestOptions.query || {};
        params.requestOptions.query.token = token;
        params.requestOptions.query.resultOffset = 50000;
        params.requestOptions.query.resultRecordCount = 50000;
      };

      const token = tokenService.getToken();
      if (token) {
        applyToken(token);
        return;
      }

      return tokenService
        .getValidToken(tokenServiceUrl, tokenClient)
        .then(applyToken);
    },
    error(error) {
      if (isLoggedOutLanding()) {
        throw error;
      }

      const status = error?.details?.httpStatus;
      const messages = JSON.stringify(error?.details?.messages || "");
      const isAuthError =
        status === 401 ||
        status === 498 ||
        status === 499 ||
        /invalid token|token required|authorization/i.test(messages);

      if (!isAuthError) {
        throw error;
      }

      console.warn("[mapEtAuth] MapET auth error, refreshing token. Status:", status);

      tokenService.clearStoredToken();
      return tokenService
        .refreshToken(tokenServiceUrl, tokenClient)
        .then((token) => {
          if (token && typeof error.retry === "function") {
            return error.retry();
          }
          throw error;
        });
    },
  });
};

export const isMapEtUrl = (url?: string): boolean =>
  Boolean(url?.includes("MapET-SFS"));

/** Fetch a MapET token if missing or stale. Returns null when unavailable. */
export const ensureMapEtToken = (
  tokenServiceUrl: string,
  client: string
): Promise<string | null> => {
  if (isLoggedOutLanding()) {
    return Promise.resolve(null);
  }
  if (!tokenService.isTokenValid()) {
    return tokenService.refreshToken(tokenServiceUrl, client);
  }
  return Promise.resolve(tokenService.getToken());
};

/** Proactively refresh MapET token before expiry. */
export const useMapEtTokenRefresh = (
  tokenServiceUrl: string,
  client: string,
  accessToken?: string
): void => {
  React.useEffect(() => {
    if (!accessToken || isLoggedOutLanding()) {
      return;
    }

    let expiryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleExpiryRefresh = () => {
      if (expiryTimeoutId !== undefined) {
        clearTimeout(expiryTimeoutId);
      }

      const msUntilRefresh = tokenService.getMsUntilRefresh();
      if (msUntilRefresh === null) {
        void tokenService.refreshToken(tokenServiceUrl, client).then(() => {
          scheduleExpiryRefresh();
        });
        return;
      }

      expiryTimeoutId = setTimeout(async () => {
        await tokenService.refreshToken(tokenServiceUrl, client);
        scheduleExpiryRefresh();
      }, msUntilRefresh);
    };

    const handleTokenUpdated = () => {
      scheduleExpiryRefresh();
    };

    void ensureMapEtToken(tokenServiceUrl, client).then(() => {
      scheduleExpiryRefresh();
    });

    const intervalId = window.setInterval(() => {
      if (!tokenService.isTokenValid()) {
        void tokenService.refreshToken(tokenServiceUrl, client);
      }
    }, 30_000);

    window.addEventListener(MAPET_TOKEN_UPDATED_EVENT, handleTokenUpdated);

    return () => {
      window.clearInterval(intervalId);
      if (expiryTimeoutId !== undefined) {
        clearTimeout(expiryTimeoutId);
      }
      window.removeEventListener(MAPET_TOKEN_UPDATED_EVENT, handleTokenUpdated);
    };
  }, [accessToken, client, tokenServiceUrl]);
};
